import { useState, useEffect } from "react";
import { 
  Bell, 
  Mail, 
  Clock, 
  Send,
  Check,
  AlertTriangle,
  DollarSign,
  Calendar,
  Settings,
  RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RemindersPanel = ({ token }) => {
  const [depositReminders, setDepositReminders] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [followUpSettings, setFollowUpSettings] = useState({
    enabled: true,
    day_2_enabled: true,
    day_5_enabled: true,
    day_10_enabled: true
  });
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState({});
  const [activeTab, setActiveTab] = useState("deposits");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [depositsRes, followUpsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/owner/deposit-reminders`, { headers }),
        axios.get(`${API}/owner/follow-ups/pending`, { headers }),
        axios.get(`${API}/owner/follow-up-settings`, { headers })
      ]);
      setDepositReminders(depositsRes.data.reminders || []);
      setFollowUps(followUpsRes.data.follow_ups || []);
      setFollowUpSettings(settingsRes.data || followUpSettings);
    } catch (err) {
      console.error("Error fetching reminders:", err);
    } finally {
      setLoading(false);
    }
  };

  const sendDepositReminder = async (projectId) => {
    setSending(prev => ({ ...prev, [`deposit_${projectId}`]: true }));
    try {
      await axios.post(`${API}/owner/deposit-reminders/${projectId}/send`, {}, { headers });
      toast.success("Deposit reminder sent");
      // Remove from list after sending
      setDepositReminders(prev => prev.filter(r => r.project_id !== projectId));
    } catch (err) {
      toast.error("Failed to send reminder");
    } finally {
      setSending(prev => ({ ...prev, [`deposit_${projectId}`]: false }));
    }
  };

  const sendFollowUp = async (projectId, followUpType) => {
    setSending(prev => ({ ...prev, [`followup_${projectId}`]: true }));
    try {
      await axios.post(`${API}/owner/follow-ups/${projectId}/send?follow_up_type=${followUpType}`, {}, { headers });
      toast.success("Follow-up sent");
      // Remove from list after sending
      setFollowUps(prev => prev.filter(f => f.project_id !== projectId));
    } catch (err) {
      toast.error("Failed to send follow-up");
    } finally {
      setSending(prev => ({ ...prev, [`followup_${projectId}`]: false }));
    }
  };

  const saveFollowUpSettings = async () => {
    try {
      await axios.put(`${API}/owner/follow-up-settings`, followUpSettings, { headers });
      toast.success("Settings saved");
    } catch (err) {
      toast.error("Failed to save settings");
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const followUpTypeLabels = {
    day_2: "2 Day Follow-up",
    day_5: "5 Day Follow-up", 
    day_10: "10 Day Follow-up"
  };

  return (
    <div className="p-6" data-testid="reminders-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reminders & Follow-ups</h1>
          <p className="text-gray-600">Automate deposit reminders and quote follow-ups</p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 shadow-sm border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <DollarSign className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{depositReminders.length}</p>
              <p className="text-sm text-gray-500">Pending Deposits</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Mail className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{followUps.length}</p>
              <p className="text-sm text-gray-500">Quote Follow-ups Due</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <Button
          variant={activeTab === "deposits" ? "default" : "outline"}
          onClick={() => setActiveTab("deposits")}
        >
          <DollarSign className="w-4 h-4 mr-2" />
          Deposit Reminders
        </Button>
        <Button
          variant={activeTab === "followups" ? "default" : "outline"}
          onClick={() => setActiveTab("followups")}
        >
          <Mail className="w-4 h-4 mr-2" />
          Quote Follow-ups
        </Button>
        <Button
          variant={activeTab === "settings" ? "default" : "outline"}
          onClick={() => setActiveTab("settings")}
        >
          <Settings className="w-4 h-4 mr-2" />
          Settings
        </Button>
      </div>

      {/* Deposit Reminders Tab */}
      {activeTab === "deposits" && (
        <div className="bg-white rounded-xl shadow-sm border">
          <div className="p-4 border-b">
            <h3 className="font-semibold text-gray-900">Outstanding Deposit Reminders</h3>
            <p className="text-sm text-gray-500">Scheduled jobs awaiting deposit payment</p>
          </div>
          
          {depositReminders.length === 0 ? (
            <div className="p-12 text-center">
              <Check className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className="text-gray-600">All deposits collected!</p>
              <p className="text-sm text-gray-400">No outstanding deposit reminders</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {depositReminders.map((reminder) => (
                <div key={reminder.project_id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-yellow-100 rounded-lg">
                      <AlertTriangle className="w-5 h-5 text-yellow-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{reminder.customer_name}</p>
                      <p className="text-sm text-gray-500">{reminder.project_type} • {reminder.email}</p>
                      {reminder.booking_date && (
                        <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                          <Calendar className="w-3 h-3" />
                          Booked: {new Date(reminder.booking_date).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-medium text-gray-900">£{(reminder.estimate_high * 0.25).toLocaleString()}</p>
                      <p className="text-xs text-gray-500">25% deposit</p>
                    </div>
                    <Button
                      onClick={() => sendDepositReminder(reminder.project_id)}
                      disabled={sending[`deposit_${reminder.project_id}`]}
                      size="sm"
                    >
                      {sending[`deposit_${reminder.project_id}`] ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-1" />
                          Send
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Follow-ups Tab */}
      {activeTab === "followups" && (
        <div className="bg-white rounded-xl shadow-sm border">
          <div className="p-4 border-b">
            <h3 className="font-semibold text-gray-900">Quote Follow-up Queue</h3>
            <p className="text-sm text-gray-500">Quotes awaiting customer response</p>
          </div>
          
          {!followUpSettings.enabled ? (
            <div className="p-12 text-center">
              <Bell className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600">Follow-up automation is disabled</p>
              <Button onClick={() => setActiveTab("settings")} variant="outline" className="mt-4">
                Enable in Settings
              </Button>
            </div>
          ) : followUps.length === 0 ? (
            <div className="p-12 text-center">
              <Check className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className="text-gray-600">All caught up!</p>
              <p className="text-sm text-gray-400">No follow-ups due at this time</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {followUps.map((followUp) => (
                <div key={followUp.project_id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Clock className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{followUp.customer_name}</p>
                      <p className="text-sm text-gray-500">{followUp.email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                          {followUp.days_old} days old
                        </span>
                        <span className="text-xs text-gray-400">{followUp.estimate_range}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                      followUp.follow_up_type === "day_10" ? "bg-red-100 text-red-700" :
                      followUp.follow_up_type === "day_5" ? "bg-yellow-100 text-yellow-700" :
                      "bg-blue-100 text-blue-700"
                    }`}>
                      {followUpTypeLabels[followUp.follow_up_type]}
                    </span>
                    <Button
                      onClick={() => sendFollowUp(followUp.project_id, followUp.follow_up_type)}
                      disabled={sending[`followup_${followUp.project_id}`]}
                      size="sm"
                    >
                      {sending[`followup_${followUp.project_id}`] ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-1" />
                          Send
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === "settings" && (
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Follow-up Automation Settings</h3>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Enable Quote Follow-up Automation</p>
                <p className="text-sm text-gray-500">Automatically queue follow-ups for unresponded quotes</p>
              </div>
              <Checkbox
                checked={followUpSettings.enabled}
                onCheckedChange={(checked) => setFollowUpSettings(prev => ({ ...prev, enabled: checked }))}
              />
            </div>

            <div className={followUpSettings.enabled ? "" : "opacity-50 pointer-events-none"}>
              <p className="text-sm font-medium text-gray-700 mb-3">Follow-up Schedule</p>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-sm font-medium text-blue-600">
                      2
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Day 2 Follow-up</p>
                      <p className="text-xs text-gray-500">Gentle reminder about quote</p>
                    </div>
                  </div>
                  <Checkbox
                    checked={followUpSettings.day_2_enabled}
                    onCheckedChange={(checked) => setFollowUpSettings(prev => ({ ...prev, day_2_enabled: checked }))}
                  />
                </div>

                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center text-sm font-medium text-yellow-600">
                      5
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Day 5 Follow-up</p>
                      <p className="text-xs text-gray-500">Second reminder with value proposition</p>
                    </div>
                  </div>
                  <Checkbox
                    checked={followUpSettings.day_5_enabled}
                    onCheckedChange={(checked) => setFollowUpSettings(prev => ({ ...prev, day_5_enabled: checked }))}
                  />
                </div>

                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center text-sm font-medium text-red-600">
                      10
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Day 10 Follow-up</p>
                      <p className="text-xs text-gray-500">Final follow-up with urgency</p>
                    </div>
                  </div>
                  <Checkbox
                    checked={followUpSettings.day_10_enabled}
                    onCheckedChange={(checked) => setFollowUpSettings(prev => ({ ...prev, day_10_enabled: checked }))}
                  />
                </div>
              </div>
            </div>

            <div className="pt-4 border-t">
              <Button onClick={saveFollowUpSettings}>
                Save Settings
              </Button>
            </div>
          </div>

          {/* Note about email provider */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-700">
              <strong>Note:</strong> Email notifications are currently logged to the console. 
              Configure SendGrid API key in environment variables to enable actual email delivery.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default RemindersPanel;
